import React, { useMemo, useState, useEffect, useRef, useLayoutEffect } from 'react';
import PropTypes from 'prop-types';
import { Table, Spin, Tooltip, message } from 'antd';
import StackIcon from 'svg/v2/newcs_dashboard_crashanalysis_issuedetail_stack_icon.svg';
import CsCollapseButton from 'components/commons/CsCollapseButton/CsCollapseButton';
import CsSimpleButton from 'components/antd-extension/CsSimpleButton';
import DownloadIcon from 'svg/v2/newcs_dashboard_download_icon_normal.svg';
import scss from '../DataHead/DataHead.scss';
import { connect } from 'react-redux';
import { ServerAppSettings } from 'utils/server-app-settings';
import moment from 'moment';
import { ExceptionCategory, ExceptionCategoryUtil } from 'utils/exception-category';
import { decodeCsSdkEncodedValue } from 'utils/helper';
import { Link } from 'react-router-dom';
import RestHelper from 'utils/RestHelper';
import gpuSourceScroll from './gpuSourceScroll';
import { downloadWithUrl } from 'utils/file-utils';
import { ze } from 'utils/zhEn';

/**
 * GpuCrashInfo 组件 - GPU崩溃信息展示组件
 *
 * 功能说明：
 * 1. 展示GPU相关的崩溃信息，包含三个区域：Shader Info、Resource Info、GPU Breadcrumbs
 * 2. 从issue对象中提取customCrashStack和gpuDumpInfoJson数据
 * 3. 支持各区域的展开折叠功能
 *
 * Props:
 * @param {object} issue - 问题对象信息，包含crashDoc等数据
 */
const GpuCrashInfo = ({
  reduxState,
  issue,
  pid,
}) => {
  const appId = reduxState.app.get('current').get('appId');
  const serverAppSettings = useMemo(() => reduxState.app.get('appIdToServerAppSettings')?.[appId] || {},
    [reduxState.app, appId]);
  const associateGpuCrashInfoWithError = !!serverAppSettings[ServerAppSettings.keys.associateGpuCrashInfoWithError];

  // 展开折叠状态管理
  const [isDeviceStatsExpanded, setIsDeviceStatsExpanded] = useState(true);
  const [isGpuBreadcrumbsExpanded, setIsGpuBreadcrumbsExpanded] = useState(true);

  // 安全获取GPU堆栈数据
  const { crashDoc } = issue ? issue.toJS() : {};
  const crashMap = crashDoc?.crashMap || {};
  const {
    crashId,
    deviceId,
    uploadTime,
    expUid: crashMapExpUid,
    productVersion,
    sdkVersion,
  } = crashMap;
  const esMap = crashDoc?.esMap || {};
  const expUid = crashMapExpUid || esMap.expUid;
  const {
    customCrashStack,
    // 类型为字符串，内容样例：{"eventMarkerInfos":[{"contextId":1,"contextStatus":1,"contextType":4,"markerDataOwnership":1,"markerDataPtr":140709508206128,"markerDataSize":64}],"resourceInfos":[],"shaderInfos":[{"internal":false,"shaderHash":8971418160378466627,"shaderInstance":4213604048,"shaderType":"COMPUTE"}],"status":"Reset"}
    gpuDumpInfoJson: gpuDumpInfoJsonStr,
  } = esMap;

  // DFM需求，从关联的错误里面查询GPU Breadcrumbs
  const [crashStackFromAssociatedError, setCrashStackFromAssociatedError] = useState('');
  const [associatedCrashId, setAssociatedCrashId] = useState('');

  useEffect(() => {
    if (crashId && associateGpuCrashInfoWithError) {
      fetchCrashStackFromAssociatedError();
    }
  }, [crashId, associateGpuCrashInfoWithError]);

  async function fetchCrashStackFromAssociatedError() {
    setCrashStackFromAssociatedError('');
    setAssociatedCrashId('');
    if (!deviceId || deviceId.toLowerCase() === 'unknown') {
      return;
    }

    const currentCrashUploadTimeMillis = moment(uploadTime).valueOf();

    const rsp = await RestHelper.mustPost('/redir/api/queryAccess/queryAccessList', {
      appId,
      deviceIdList: [deviceId],
      pageNumber: 1,
      pageSize: 1000,
      skipDistinctQuery: true,
      exceptionCategoryList: [ExceptionCategory.CRASH, ExceptionCategory.ERROR],
      uploadTimeEndMillis: currentCrashUploadTimeMillis + 10_000,
      uploadTimeBeginMillis: currentCrashUploadTimeMillis - 86400_000,
    });
    const { records } = rsp.json.data?.result || [];
    const currentRecord = records.find(x => x.crashId === crashId);
    if (currentRecord) {
      const currentRecordLaunchTimeMillisStr = currentRecord.launchTimeMillisStr;
      // 过滤launchTimeMillisStr相同的所有错误上报
      const sameSessionRecords = records.filter(x => {
        return x.launchTimeMillisStr === currentRecordLaunchTimeMillisStr && ExceptionCategoryUtil.isError(x.csType);
      });
      // 查找带GPU字样的上报
      const matchedRecord = sameSessionRecords.find(x => x.stack?.includes('GPU'));
      if (matchedRecord) {
        const stackEncoded = matchedRecord.stack;
        const stack = decodeCsSdkEncodedValue(stackEncoded);
        setCrashStackFromAssociatedError(stack);
        setAssociatedCrashId(matchedRecord.crashId);
      }
    }
  }

  // 用useMemo计算gpuDumpInfo，处理64位整型转换
  const gpuDumpInfo = useMemo(() => {
    // 如果 gpuDumpInfoJsonStr 为空或者不是字符串，返回 null
    if (!gpuDumpInfoJsonStr || typeof gpuDumpInfoJsonStr !== 'string') {
      return null;
    }

    try {
      // 在JSON.parse之前，使用正则表达式将shaderHash和shaderInstance字段的数值转换为字符串，这两个数值是int64
      // 这样可以避免JavaScript的Number类型精度限制（只能安全表示 -(2^53-1) 到 (2^53-1) 之间的整数）
      // 后续需要使用bigInt处理
      let processedJsonStr = gpuDumpInfoJsonStr
        .replace(/"shaderHash"\s*:\s*(-?\d+)/g, '"shaderHash":"$1"')
        .replace(/"shaderInstance"\s*:\s*(-?\d+)/g, '"shaderInstance":"$1"');

      const parsed = JSON.parse(processedJsonStr);

      // 为shaderInfos中的每个item添加shaderHashHex和shaderInstanceHex字段
      if (parsed.shaderInfos && Array.isArray(parsed.shaderInfos)) {
        parsed.shaderInfos = parsed.shaderInfos.map(item => {
          // 处理shaderHash字段
          if (item.shaderHash) {
            try {
              // 将64位有符号整型转换为十六进制
              let bigIntValue = BigInt(item.shaderHash);

              // 如果是负数，转换为对应的无符号64位整数
              if (bigIntValue < 0n) {
                bigIntValue = bigIntValue + (1n << 64n);
              }

              item.shaderHashHex = '0x' + bigIntValue.toString(16).toUpperCase();
            } catch (error) {
              console.warn('Failed to convert shaderHash to hex:', error);
              item.shaderHashHex = 'N/A';
            }
          }

          // 处理shaderInstance字段
          if (item.shaderInstance) {
            try {
              // 将64位有符号整型转换为十六进制
              let bigIntValue = BigInt(item.shaderInstance);

              // 如果是负数，转换为对应的无符号64位整数
              if (bigIntValue < 0n) {
                bigIntValue = bigIntValue + (1n << 64n);
              }

              item.shaderInstanceHex = '0x' + bigIntValue.toString(16).toUpperCase();
            } catch (error) {
              console.warn('Failed to convert shaderInstance to hex:', error);
              item.shaderInstanceHex = 'N/A';
            }
          }

          return item;
        });
      }

      return parsed;
    } catch (error) {
      // JSON 解析失败时返回 null
      console.warn('Failed to parse GPU dump info JSON:', error);
      return null;
    }
  }, [gpuDumpInfoJsonStr]);

  const hasGpuDumpInfo = !!gpuDumpInfo;

  const [windowsDecode, setWindowsDecode] = useState(null);
  const [windowsDecodeLoading, setWindowsDecodeLoading] = useState(false);
  const [windowsDecodeError, setWindowsDecodeError] = useState('');
  const [bundleDownloading, setBundleDownloading] = useState(false);
  const sourcePreRef = useRef(null);
  const highlightLineRef = useRef(null);

  useLayoutEffect(() => {
    // React 16 不会把 setWindowsDecode 和 finally 里的 loading=false 合成一次渲染。
    // decode 写入时 <pre> 还没挂上，必须等 loading 结束后再滚。
    if (gpuSourceScroll.shouldDeferGpuSourceScroll({ windowsDecodeLoading })) {
      return undefined;
    }
    const run = () => gpuSourceScroll.scrollGpuSourceHighlight(sourcePreRef.current, highlightLineRef.current);
    if (run()) {
      return undefined;
    }
    const pre = sourcePreRef.current;
    if (!pre || typeof ResizeObserver === 'undefined') {
      return undefined;
    }
    const ro = new ResizeObserver(() => {
      if (run()) {
        ro.disconnect();
      }
    });
    ro.observe(pre);
    return () => ro.disconnect();
  }, [windowsDecode, windowsDecodeLoading]);

  useEffect(() => {
    setWindowsDecode(null);
    setWindowsDecodeError('');
    setWindowsDecodeLoading(false);
    if (!associateGpuCrashInfoWithError || !hasGpuDumpInfo || !crashId || !expUid || !productVersion) {
      return undefined;
    }
    let cancelled = false;
    async function fetchWindowsDecode() {
      setWindowsDecodeLoading(true);
      const shaderHashHex = gpuDumpInfo?.shaderInfos?.[0]?.shaderHashHex || '';
      const shaderHash = String(shaderHashHex).replace(/^0x/i, '');
      try {
        const rsp = await RestHelper.post('/redir/api/crash/decodeGpuDump', {
          appId,
          crashId,
          pid: (pid === undefined || pid === null || pid === '') ? undefined : Number(pid),
          expUid,
          productVersion,
          sdkVersion,
          shaderHash,
        }, { silentFailure: true });
        if (cancelled) {
          return;
        }
        const payload = rsp?.json?.data;
        if (!payload || rsp?.json?.status >= 400 || rsp?.json?.code >= 400) {
          setWindowsDecodeError(rsp?.json?.message || 'windows gpu decode failed');
          return;
        }
        const warps = payload.activeWarps || payload.result?.activeWarps || [];
        setWindowsDecode({
          ...payload,
          activeWarps: warps,
        });
      } catch (e) {
        if (!cancelled) {
          setWindowsDecodeError(e?.message || 'windows gpu decode unreachable');
        }
      } finally {
        if (!cancelled) {
          setWindowsDecodeLoading(false);
        }
      }
    }
    fetchWindowsDecode();
    return () => {
      cancelled = true;
    };
  }, [
    associateGpuCrashInfoWithError,
    hasGpuDumpInfo,
    crashId,
    expUid,
    productVersion,
    appId,
    pid,
    sdkVersion,
    gpuDumpInfo,
  ]);

  async function downloadGpuDumpBundle() {
    if (bundleDownloading) {
      return;
    }
    if (!crashId || !expUid || !productVersion) {
      message.error(ze('缺少下载 GPU dump 所需的 crashId / expUid / 版本', 'Missing crashId / expUid / version for GPU dump download'));
      return;
    }
    const shaderHashHex = gpuDumpInfo?.shaderInfos?.[0]?.shaderHashHex || '';
    const shaderHash = String(shaderHashHex).replace(/^0x/i, '');
    const requestLocalUserId = reduxState.user.get('current').get('newUserId') || '';
    setBundleDownloading(true);
    try {
      const rsp = await RestHelper.mustPost('/redir/api/crash/downloadGpuDumpBundle', {
        appId,
        crashId,
        pid: (pid === undefined || pid === null || pid === '') ? undefined : Number(pid),
        expUid,
        productVersion,
        sdkVersion,
        shaderHash,
        requestLocalUserId,
      });
      const url = rsp.json?.data?.url;
      if (!url) {
        throw new Error('gpu dump download url is empty');
      }
      downloadWithUrl(rsp.json.data.fileName || `gpu-dump-${crashId}.zip`, url);
    } finally {
      setBundleDownloading(false);
    }
  }

  const decodedWarp = windowsDecode?.activeWarps?.[0];

  // 处理堆栈数据，过滤空行
  const lines = (crashStackFromAssociatedError || customCrashStack || '').split('\n').filter(line => line.trim() !== '');

  // Shader Info 区域的列定义
  const shaderInfoColumns = [
    {
      title: 'Shader Hash',
      dataIndex: 'shaderHashHex',
      key: 'shaderHashHex',
      render: (text) => (
        <span style={{
          fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace'
        }}>
          {text || 'N/A'}
        </span>
      ),
    },
    {
      title: 'Shader Type',
      dataIndex: 'shaderType',
      key: 'shaderType',
      render: (text) => text || 'N/A',
    },
    {
      title: 'Shader Instance',
      dataIndex: 'shaderInstanceHex',
      key: 'shaderInstanceHex',
      render: (text) => (
        <span style={{
          fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace'
        }}>
          {text || 'N/A'}
        </span>
      ),
    },
  ];

  // Shader Info 区域
  const shaderInfoSection = hasGpuDumpInfo && gpuDumpInfo.shaderInfos && gpuDumpInfo.shaderInfos.length > 0 ? (
    <div style={{ marginTop: '12px' }}>
      <div style={{ marginBottom: '8px' }}>Shader Info</div>
      <Table
        columns={shaderInfoColumns}
        dataSource={gpuDumpInfo.shaderInfos.map((item, index) => ({ ...item, key: index }))}
        pagination={false}
        size="small"
        bordered
      />
    </div>
  ) : null;

  const renderFunctionSource = (warp) => {
    const snippetLines = warp?.callstack?.[0]?.sourceCode?.lines;
    if (Array.isArray(snippetLines) && snippetLines.length > 0) {
      return (
        <pre
          ref={sourcePreRef}
          style={{
          margin: 0,
          padding: '8px 12px',
          background: '#fafafa',
          border: '1px solid #f0f0f0',
          overflow: 'auto',
          maxHeight: '420px',
          position: 'relative',
          fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace',
          fontSize: '13px',
          lineHeight: '20px',
        }}>
          {snippetLines.map((item, i) => (
            <div
              key={i}
              ref={item.highlight ? highlightLineRef : undefined}
              style={item.highlight ? { background: '#fff1b8' } : undefined}
            >
              {`${item.lineNumber}\t${item.text || ''}`}
            </div>
          ))}
        </pre>
      );
    }
    if (!warp?.functionSource) {
      return (
        <div style={{ color: '#999', fontSize: '13px' }}>
          PDB 未内嵌 HLSL/usf 源码，仅展示文件与行号
        </div>
      );
    }
    return (
      <pre style={{
        margin: 0,
        padding: '8px 12px',
        background: '#fafafa',
        border: '1px solid #f0f0f0',
        overflow: 'auto',
        maxHeight: '420px',
        fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace',
        fontSize: '13px',
        lineHeight: '20px',
        whiteSpace: 'pre-wrap',
      }}>
        {warp.functionSource}
      </pre>
    );
  };

  const windowsDecodeSection = (windowsDecodeLoading || decodedWarp || windowsDecodeError) ? (
    <div style={{ marginTop: '12px' }}>
      <div style={{ marginBottom: '8px' }}>Shader Source (Windows Decode)</div>
      {windowsDecodeLoading && <Spin size="small" />}
      {!windowsDecodeLoading && windowsDecodeError && (
        <div style={{ color: '#999', fontSize: '13px' }}>
          {gpuSourceScroll.formatGpuWindowsDecodeError(windowsDecodeError)}，不影响上方 Shader Info。
        </div>
      )}
      {!windowsDecodeLoading && decodedWarp && (
        <div>
          <div style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', marginBottom: '8px' }} className={scss.dataHead}>
            <div>
              <span className={scss.fieldName}>Function: </span>
              <span className={scss.fieldValueNoWrap} style={{
                fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace'
              }}>
                {decodedWarp.functionName || 'Unknown'}
              </span>
            </div>
            <div>
              <span className={scss.fieldName}>Location: </span>
              <span className={scss.fieldValueNoWrap} style={{
                fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace'
              }}>
                {decodedWarp.shaderLocation || 'N/A'}
              </span>
            </div>
          </div>
          {renderFunctionSource(decodedWarp)}
        </div>
      )}
    </div>
  ) : null;

  // Resource Info 区域的列定义
  const resourceInfoColumns = [
    {
      title: 'Resource Debug Name',
      dataIndex: 'debugName',
      key: 'debugName',
    },
    {
      title: 'Resource Residency',
      dataIndex: 'residency',
      key: 'residency',
    },
  ];

  // Resource Info 区域（占位表格）
  const resourceInfoSection = hasGpuDumpInfo && gpuDumpInfo?.resourceInfos?.length > 0 && (
    <div style={{ marginTop: '12px' }}>
      <div style={{ marginBottom: '8px' }}>Resource Info</div>
      <Table
        columns={resourceInfoColumns}
        dataSource={gpuDumpInfo?.resourceInfos || []}
        pagination={false}
        size="small"
        bordered
        locale={{ emptyText: '暂无数据' }}
      />
    </div>
  );

  // GPU Breadcrumbs 区域内容
  const gpuBreadcrumbsContent = lines.length > 0 && (
    <div style={{ marginTop: '12px' }}>
      <table style={{ borderCollapse: 'collapse', width: '100%' }}>
        <tbody style={{
          borderCollapse: 'collapse',
          width: '100%',
          border: '1px solid #f6f6f6'
        }}>
        {lines.map((line, i) => (
          <tr key={i}>
            <td style={{
              padding: '4px',
              border: '1px solid #f6f6f6',
              whiteSpace: 'pre-wrap',
              fontSize: '14px',
              color: '#4c4c4c',
              fontFamily: 'Consolas, Menlo, Monaco, "DejaVu Sans Mono", "Courier New", monospace'
            }}>
              {line}
            </td>
          </tr>
        ))}
        </tbody>
      </table>
    </div>
  );

  return (
    <div>
      {/* Device Stats 区域 */}
      {hasGpuDumpInfo && (
        <div>
          {/* Device Stats 头部 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
              border: '1px solid #EBEBEB',
              padding: '14px 16px',
              marginTop: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <StackIcon />
              <div>
                <div>GPU Dump Info</div>
                <div style={{ display: 'flex', gap: '4px', alignItems:'center' }} className={scss.dataHead}>
                  <div className={scss.fieldName}>Device Stats: </div>
                  <div className={scss.fieldValueNoWrap}>{gpuDumpInfo?.status}</div>
                </div>
              </div>
            </div>
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <Tooltip title={ze('下载 nv-gpudmp / nvdbg / dxil / pdb 组合包', 'Download nv-gpudmp / nvdbg / dxil / pdb bundle')}>
                <CsSimpleButton
                  disabled={bundleDownloading}
                  onClick={downloadGpuDumpBundle}
                >
                  <DownloadIcon />
                </CsSimpleButton>
              </Tooltip>
              <CsCollapseButton
                isCollapsed={!isDeviceStatsExpanded}
                onClick={() => setIsDeviceStatsExpanded(!isDeviceStatsExpanded)}
              />
            </div>
          </div>

          {/* Device Stats 内容 */}
          {isDeviceStatsExpanded && (
            <div style={{ padding: '0 16px' }}>
              {shaderInfoSection}
              {windowsDecodeSection}
              {resourceInfoSection}
            </div>
          )}
        </div>
      )}

      {/* GPU Breadcrumbs 区域 */}
      {lines.length > 0 && (
        <div>
          {/* GPU Breadcrumbs 头部 */}
          <div
            style={{
              display: 'flex',
              alignItems: 'center',
              justifyContent: 'space-between',
              gap: '24px',
              border: '1px solid #EBEBEB',
              padding: '14px 16px',
              marginTop: '20px'
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '24px' }}>
              <StackIcon />
              <div>GPU Breadcrumbs</div>
            </div>
            <CsCollapseButton
              isCollapsed={!isGpuBreadcrumbsExpanded}
              onClick={() => setIsGpuBreadcrumbsExpanded(!isGpuBreadcrumbsExpanded)}
            />
          </div>

          {/* GPU Breadcrumbs 内容 */}
          {isGpuBreadcrumbsExpanded && (
            <div style={{ padding: '0 16px' }}>
              {gpuBreadcrumbsContent}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

GpuCrashInfo.propTypes = {
  reduxState: PropTypes.object,
  issue: PropTypes.object,
  pid: PropTypes.number,
};

const mapStateToProps = (state, router) => ({
  reduxState: state,
});

export default connect(mapStateToProps)(GpuCrashInfo);
